/**
 * Pure label-range helpers for multi-label separation (source-agnostic).
 * Used by any country/module that submits PUT { ranges: [...] } with 1-based page ranges.
 */
import {
  LabelRangeDraft,
  LabelRangePayload,
  LabelRangeValidationResult,
  VariantRow
} from './types';

let rangeIdCounter = 0;

/** Stable React list key for a new form row (not sent to the API). */
export const createRangeId = (): string => {
  rangeIdCounter += 1;
  return `label-range-${Date.now()}-${rangeIdCounter}`;
};

/** One blank label block for the form (start/end empty, optional name). */
export const createEmptyRange = (): LabelRangeDraft => ({
  id: createRangeId(),
  startPage: '',
  endPage: '',
  labelName: ''
});

/**
 * Map GET-detail variants into editable form rows.
 * Returns a single empty row when nothing is saved yet.
 */
export const variantsToDrafts = (variants: VariantRow[]): LabelRangeDraft[] => {
  if (!variants.length) {
    return [createEmptyRange()];
  }

  return variants.map(variant => ({
    id: createRangeId(),
    startPage: String(variant.start_page),
    endPage: String(variant.end_page),
    labelName: variant.label_name ?? ''
  }));
};

type PageParseResult = { ok: true; value: number } | { ok: false; message: string };

const parseRequiredPage = (
  value: string,
  labelIndex: number,
  fieldName: string
): PageParseResult => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { ok: false, message: `Label ${labelIndex}: ${fieldName} is required` };
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return { ok: false, message: `Label ${labelIndex}: ${fieldName} must be a whole number` };
  }

  return { ok: true, value: parsed };
};

/**
 * Validate form drafts and build the PUT body.
 *
 * Rules (aligned with backend):
 * - At least one range; start_page >= 1; end_page >= start_page
 * - label_name is omitted when blank
 * - When multiple ranges and pageCount is known: no end_page may exceed pageCount
 *   (single-range submissions skip the document-length cap on the client)
 */
export const validateAndParseRanges = (
  drafts: LabelRangeDraft[],
  pageCount: number | null
): LabelRangeValidationResult => {
  if (!drafts.length) {
    return { ok: false, message: 'At least one label range is required' };
  }

  const payload: LabelRangePayload[] = [];

  for (let index = 0; index < drafts.length; index += 1) {
    const draft = drafts[index];
    const labelIndex = index + 1;

    const startResult = parseRequiredPage(draft.startPage, labelIndex, 'Start page');
    if (!startResult.ok) {
      return { ok: false, message: startResult.message };
    }

    const endResult = parseRequiredPage(draft.endPage, labelIndex, 'End page');
    if (!endResult.ok) {
      return { ok: false, message: endResult.message };
    }

    const startPage = startResult.value;
    const endPage = endResult.value;

    if (startPage < 1) {
      return { ok: false, message: `Label ${labelIndex}: Start page must be at least 1` };
    }

    if (endPage < startPage) {
      return {
        ok: false,
        message: `Label ${labelIndex}: End page must be greater than or equal to start page`
      };
    }

    const range: LabelRangePayload = {
      start_page: startPage,
      end_page: endPage
    };

    const labelName = draft.labelName.trim();
    if (labelName) {
      range.label_name = labelName;
    }

    payload.push(range);
  }

  if (payload.length > 1 && pageCount != null) {
    const exceedsPageCount = payload.some(range => range.end_page > pageCount);
    if (exceedsPageCount) {
      return {
        ok: false,
        message: `Page range exceeds document length (${pageCount} pages)`
      };
    }
  }

  return { ok: true, payload };
};
